import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import styled from 'styled-components'
import Card from '~/components/card'
import { toastError, toastSuccess } from '~/components/toasts'
import { colors } from '~/design'
import { Button } from '~/prix/react/components/button'
import NumberField from '~/prix/react/components/form/field/numberField'
import StringField from '~/prix/react/components/form/field/stringField'
import useAction from '~/prix/react/hooks/action'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import emergencyDispatchCreateDefinition from './create/emergencyDispatchCreate.definition'
import emergencyDispatchFetchDefinition from './fetch/emergencyDispatchFetch.definition'
import emergencyDispatchUpdateDefinition from './update/emergencyDispatchUpdate.definition'

type EmergencyDispatchFormData = {
  plusCode: string
  title: string
  observation: string | null
  latitude: number | null
  longitude: number | null
  residenceId: string | null
  fullAddress: string
  status: string
}

const emptyFormData: EmergencyDispatchFormData = {
  plusCode: '',
  title: '',
  observation: '',
  latitude: null,
  longitude: null,
  residenceId: null,
  fullAddress: '',
  status: 'Aberto',
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.padding(2)};
  margin-bottom: ${({ theme }) => theme.padding(2)};

  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(2)};
    padding: 10px 15px;
  }

  .field-row {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.padding(2)};
  }

  .react-datepicker-wrapper,
  input,
  textarea {
    width: 100%;
  }

  .button-row {
    display: flex;
    justify-content: center;
    gap: ${({ theme }) => theme.padding(2)};
    padding: ${({ theme }) => theme.padding(2)} 0 ${({ theme }) => theme.padding(3)} 0;
    width: 100%;
  }

  button {
    height: 45px;
    min-width: 160px;
    border-radius: 8px;
    font-weight: 700;
  }

  .save-button {
    background-color: ${colors.prixOrange};
  }

  @media (max-width: 768px) {
    .field-row {
      grid-template-columns: 1fr;
    }
  }
`

function normalizeNullableText(value: string | null) {
  const normalizedValue = value?.trim()
  return normalizedValue ? normalizedValue : null
}

export default function EmergencyDispatchFormScreen() {
  const navigate = useNavigate()
  const { id: emergencyDispatchIdentifier } = useParams()
  const isEditing = Boolean(emergencyDispatchIdentifier)
  const [formData, setFormData] = useState<EmergencyDispatchFormData>(emptyFormData)

  const fetchInput = useMemo(
    () => ({
      id: emergencyDispatchIdentifier || '',
    }),
    [emergencyDispatchIdentifier],
  )

  const emergencyDispatchFetch = useAction(
    emergencyDispatchFetchDefinition,
    fetchInput,
    [fetchInput.id],
    {
      autoLoad: isEditing,
      staleWhileRevalidate: false,
      onError: error => toastError(error.message || 'Erro ao carregar atendimento'),
    },
  )

  const { callAction: callCreateEmergencyDispatch, isLoading: isCreating } = useActionFunction(
    emergencyDispatchCreateDefinition,
    { initialIsLoading: false },
  )
  const { callAction: callUpdateEmergencyDispatch, isLoading: isUpdating } = useActionFunction(
    emergencyDispatchUpdateDefinition,
    { initialIsLoading: false },
  )

  useEffect(() => {
    const emergencyDispatch = emergencyDispatchFetch.result
    if (!emergencyDispatch) {
      return
    }

    setFormData({
      plusCode: emergencyDispatch.plusCode,
      title: emergencyDispatch.title,
      observation: emergencyDispatch.observation ?? '',
      latitude: emergencyDispatch.latitude,
      longitude: emergencyDispatch.longitude,
      residenceId: emergencyDispatch.residenceId ?? null,
      fullAddress: emergencyDispatch.fullAddress,
      status: emergencyDispatch.status,
    })
  }, [emergencyDispatchFetch.result])

  const handleInputChange = useCallback(
    (field: keyof EmergencyDispatchFormData, value: string | number | null) => {
      setFormData(currentFormData => ({
        ...currentFormData,
        [field]: value,
      }))
    },
    [],
  )

  const handleSubmit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()

      if (formData.latitude === null || formData.longitude === null) {
        toastError('Informe latitude e longitude do atendimento')
        return
      }

      const payload = {
        ...formData,
        observation: normalizeNullableText(formData.observation),
        residenceId: normalizeNullableText(formData.residenceId),
        latitude: formData.latitude,
        longitude: formData.longitude,
      }

      const actionCall = isEditing
        ? callUpdateEmergencyDispatch({
            id: emergencyDispatchIdentifier!,
            ...payload,
            updatedAt: new Date().toISOString(),
          })
        : callCreateEmergencyDispatch(payload)

      actionCall.resultPromise
        .then(() => {
          toastSuccess(
            isEditing
              ? 'Atendimento atualizado com sucesso!'
              : 'Atendimento criado com sucesso!',
          )
          navigate('/app/attendances', { replace: true })
        })
        .catch(error => {
          toastError(error.message || 'Erro ao salvar atendimento')
        })
    },
    [
      callCreateEmergencyDispatch,
      callUpdateEmergencyDispatch,
      emergencyDispatchIdentifier,
      formData,
      isEditing,
      navigate,
    ],
  )

  const isSaving = isCreating || isUpdating

  return (
    <Card title={isEditing ? 'Editar atendimento' : 'Criar atendimento'}>
      <Wrapper>
        <form>
          <StringField
            fieldSchema={{
              currentMeta: { label: 'Título' },
              currentRules: { isRequired: true },
            }}
            value={formData.title}
            onDirectChange={value => handleInputChange('title', value)}
          />
          <StringField
            fieldSchema={{
              currentMeta: { label: 'Observações', lines: 4 },
              currentRules: { isRequired: false },
            }}
            value={formData.observation}
            onDirectChange={value => handleInputChange('observation', value)}
          />
          <StringField
            fieldSchema={{
              currentMeta: { label: 'Endereço completo' },
              currentRules: { isRequired: true },
            }}
            value={formData.fullAddress}
            onDirectChange={value => handleInputChange('fullAddress', value)}
          />
          <div className='field-row'>
            <StringField
              fieldSchema={{
                currentMeta: { label: 'Plus code' },
                currentRules: { isRequired: true },
              }}
              value={formData.plusCode}
              onDirectChange={value => handleInputChange('plusCode', value)}
            />
            <StringField
              fieldSchema={{
                currentMeta: { label: 'Status' },
                currentRules: { isRequired: true },
              }}
              value={formData.status}
              onDirectChange={value => handleInputChange('status', value)}
            />
          </div>
          <div className='field-row'>
            <NumberField
              label='Longitude'
              value={formData.longitude}
              onDirectChange={value => handleInputChange('longitude', value)}
            />
            <NumberField
              label='Latitude'
              value={formData.latitude}
              onDirectChange={value => handleInputChange('latitude', value)}
            />
          </div>
          <StringField
            fieldSchema={{
              currentMeta: { label: 'Residência vinculada' },
              currentRules: { isRequired: false },
            }}
            value={formData.residenceId}
            onDirectChange={value => handleInputChange('residenceId', value)}
          />
        </form>
        <div className='button-row'>
          <Button onClick={() => navigate('/app/attendances')}>Cancelar</Button>
          <Button className='save-button' disabled={isSaving} onClick={handleSubmit}>
            {isEditing ? 'Salvar atendimento' : 'Criar atendimento'}
          </Button>
        </div>
      </Wrapper>
    </Card>
  )
}
